import type { Map } from "maplibre-gl";
import type { CountryChart, MapChartModel } from "../../core/types/charts";
import { ChartModelParser } from "./ChartModelParser";
import { ChartDisplaySettingsStore } from "./ChartDisplaySettings";
import { ChartPopupManager } from "./ChartPopupManager";

interface ChartMatch { chart: CountryChart | null; featureName: string; }

/** Coordinates chart matching, the shared legend, and popup manager without coupling the Viewer page to popup DOM. */
export class ChartService {
  private model: MapChartModel = ChartModelParser.createTemplate(); private manager: ChartPopupManager | null = null;
  readonly displaySettings = new ChartDisplaySettingsStore();
  initialize(host: HTMLElement, map: Map): void { this.manager?.destroy(); this.manager = new ChartPopupManager(host, map); this.displaySettings.subscribe((settings) => this.manager?.updateAll(settings)); }
  destroy(): void { this.manager?.destroy(); this.manager = null; }
  setModel(model: MapChartModel): void { this.model = model; }
  getModel(): MapChartModel { return this.model; }
  openForProperties(properties: Record<string, unknown> | undefined, lngLat?: { lng: number; lat: number }): void {
    const match = this.findChartForFeature(properties);
    const chart = match.chart ?? { country_name: "", properties: [] };
    this.manager?.open(match.featureName || "Unnamed feature", chart, this.model, this.displaySettings.get(), properties ?? {}, lngLat);
  }
  closeAll(): void { this.manager?.closeAll(); }
  closeFocused(): void { this.manager?.closeFocused(); }
  drawLegend(container: HTMLElement): void {
    const legend = document.createElement("div"); legend.className = "map-legend";
    const title = document.createElement("strong"); title.textContent = this.model.legend_title; legend.append(title);
    const entries = document.createElement("div"); entries.className = "map-legend__entries";
    this.model.legends.forEach((item) => { const row = document.createElement("span"); const color = document.createElement("i"); color.style.background = item.color; row.append(color, document.createTextNode(item.name)); entries.append(row); });
    legend.append(entries);
    container.replaceChildren(legend);
  }
  private findChartForFeature(properties: Record<string, unknown> | undefined): ChartMatch {
    const featureName = this.extractFeatureName(properties); const normalized = this.normalizeName(featureName);
    if (normalized) return { featureName, chart: this.model.charts.find((item) => this.normalizeName(item.country_name) === normalized) ?? null };
    const usable = this.model.charts.filter((item) => this.normalizeName(item.country_name));
    return usable.length === 1 ? { featureName: usable[0].country_name, chart: usable[0] } : { featureName: "", chart: null };
  }
  private extractFeatureName(properties: Record<string, unknown> | undefined): string { for (const [key, value] of Object.entries(properties ?? {})) if (["name", "country_name", "countryname", "title", "label"].includes(key.toLowerCase()) && (typeof value === "string" || typeof value === "number")) return String(value).trim(); return ""; }
  private normalizeName(value: unknown): string { return String(value ?? "").trim().toLowerCase(); }
}
